import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store/authStore';
import { canSignIn } from '../../../config/loginRoles';

// "Sign in as user" is only for the customers' own accounts, never for an admin account, whatever the token says.
const OPENABLE_ROLES = ['OWNER', 'OPERATOR'];

/**
 * "Sign in as user" from the super admin panel: the panel opens /panel-access#token=<JWT> for an owner it minted a 60-minute session for.
 * The token is read from the URL fragment and removed from the address bar at once, checked with one direct request
 * (not the app's api client: its 401 handler would sign out and jump to /login before a reason could be shown),
 * then stored exactly as a normal login stores it.
 */
export default function PanelAccessPage() {
    const navigate = useNavigate();
    const [problem, setProblem] = useState(null);
    // Read once, without touching the URL: React StrictMode (dev) runs the effect twice, and the second run must still find the token.
    const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token'));

    useEffect(() => {
        window.history.replaceState(null, '', window.location.pathname);
        if (!token) {
            setProblem('No access token in the link. Open this page from the super admin panel.');
            return;
        }

        let cancelled = false;
        fetch(`${import.meta.env.VITE_API_URL || ''}/api/auth/me`, { headers: { Authorization: `Bearer ${token}`, 'X-Client': 'web' } })
            .then(async (res) => {
                const body = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(`The server answered ${res.status}: ${body.error ?? 'no message'}`);
                const user = body.data;
                if (!OPENABLE_ROLES.includes(user?.role) || !canSignIn(user.role)) throw new Error('This account cannot be opened here.');
                if (cancelled) return;
                useAuthStore.setState({ user, token, isAuthenticated: true });
                navigate('/', { replace: true });
            })
            .catch((error) => !cancelled && setProblem(error.message));
        return () => {
            cancelled = true;
        };
    }, [navigate, token]);

    return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
            {problem ? (
                <>
                    <p role="alert" className="max-w-md text-sm text-red-600">{problem}</p>
                    <Link to="/login" className="text-sm underline">Go to login</Link>
                </>
            ) : (
                <p className="text-sm text-gray-600">Signing you in…</p>
            )}
        </div>
    );
}
